import React, { ReactNode } from "react";
import { getChallengePropertyData } from "@/services/ChallengeService";
import { useQuery } from "@tanstack/react-query";
import { get, isNil } from "lodash";
import { useSearchParams } from "react-router-dom";
import { useUser } from "@/context/UserContext";
import { useAuth0 } from "@auth0/auth0-react";

interface ChallengeData {
    challenger: {
        user_id: string;
        referral_code: string;
    };
}

interface ChallengeContextType {
    isChallenged: boolean;
    challengeProperties: ChallengeData | Record<string, never>;
    challengeId: string | null;
    challengeQuery: any; // TODO: type this more specifically based on the query return type
    isChallengeURL: boolean;
    isInvalidChallengURL: boolean;
    isOwnChallenge: boolean;
}

export const ChallengeContext = React.createContext<ChallengeContextType | undefined>(undefined);

interface ChallengeProviderProps {
    children: ReactNode;
}

export const ChallengeProvider = ({ children }: ChallengeProviderProps) => {
    const [searchParams] = useSearchParams();
    const { userState } = useUser();
    const { isAuthenticated } = useAuth0();
    const userId = get(userState, "id", null) as string | null;
    // Get challenge_id from search param
    let challenge_id = searchParams.get("challenge_id") ?? null;
    const isChallengeURL = searchParams.has("challenge_id");

    if (!challenge_id) {
        // if it doesn't exist in query param, find in local storage
        challenge_id = localStorage.getItem("challenge_id") ?? null;
        // get the referral code using the challenge id
    } else {
        // we store it to localstorage, in case user closes the browser
        localStorage.setItem("challenge_id", challenge_id);
    }

    const challengePropertiesData = useQuery({
        queryKey: ["challengePropertiesData", challenge_id, userId, isAuthenticated],
        queryFn: () => {
            if (!challenge_id) {
                throw new Error("Challenge ID is required");
            }
            return getChallengePropertyData(String(challenge_id), userId ?? "", isAuthenticated);
        },
        enabled: challenge_id !== null,
    });

    const challengeData = get(challengePropertiesData, "data.data", null) as ChallengeData | null;

    if (!challengeData) {
        // challenge do not exist remove in local storage
        localStorage.removeItem("challenge_id");
    } else {
        const referral_code = get(challengeData, "challenger.referral_code", "") as string;
        if (referral_code && referral_code.trim() !== "") {
            // referral code exists
            localStorage.setItem("referral_code", referral_code);
        }
    }

    const isFetching = challengePropertiesData?.isFetching || false;
    const isSuccess = challengePropertiesData?.isSuccess || false;

    return (
        <ChallengeContext.Provider
            value={{
                isChallenged: challenge_id !== null && !isNil(challengeData), // do not show modal when challenge do not exist
                challengeProperties: challenge_id !== null && challengeData ? challengeData : {},
                challengeId: challenge_id,
                challengeQuery: challengePropertiesData,
                isChallengeURL,
                isInvalidChallengURL: !isFetching && !isSuccess,
                isOwnChallenge: isAuthenticated && userId === challengeData?.challenger?.user_id,
            }}
        >
            {children}
        </ChallengeContext.Provider>
    );
};

export const useChallenge = (): ChallengeContextType => {
    const context = React.useContext(ChallengeContext);
    if (context === undefined) {
        throw new Error("useChallenge must be used within a ChallengeProvider");
    }
    return context;
};
